import React, { useState, useEffect, useMemo } from "react";
import {
  ResponsiveContainer, ScatterChart, Scatter, XAxis, YAxis, ZAxis,
  ReferenceLine, ReferenceArea, Tooltip, LabelList, Cell,
} from "recharts";
import { searchStocks } from "../lib/krStocks";
import { computeFlowTrajectory } from "../lib/flows";
import RelativeStrength from "./RelativeStrength";

const WINDOWS = [[20, "20일"], [40, "40일"], [60, "60일"]];

function StockSearch({ onPick }) {
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const matches = open && q ? searchStocks(q, 8) : [];
  return (
    <div style={{ position: "relative", width: "min(320px, 60vw)" }}>
      <input value={q} placeholder="종목명 검색 (예: 삼성전자)"
        onChange={e => { setQ(e.target.value); setOpen(true); }}
        onFocus={() => setOpen(true)} onBlur={() => setTimeout(() => setOpen(false), 150)}
        style={{ width: "100%", padding: "9px 12px", borderRadius: 8, border: "1px solid #ddd", fontSize: 14, boxSizing: "border-box" }} />
      {matches.length > 0 && (
        <div style={{ position: "absolute", top: "100%", left: 0, right: 0, zIndex: 20, background: "#fff", border: "1px solid #e2e2e2", borderRadius: 8, marginTop: 3, maxHeight: 260, overflowY: "auto", boxShadow: "0 6px 20px rgba(0,0,0,0.12)" }}>
          {matches.map(m => (
            <div key={m.c} onMouseDown={() => { onPick(m); setQ(""); setOpen(false); }}
              style={{ padding: "8px 12px", cursor: "pointer", fontSize: 13 }}
              onMouseEnter={e => e.currentTarget.style.background = "#f2f5fb"}
              onMouseLeave={e => e.currentTarget.style.background = "#fff"}>
              <b>{m.n}</b> <span style={{ color: "#aaa" }}>{m.c}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default function FlowsTab() {
  const [sel, setSel] = useState(null);          // {n,c,m}
  const [series, setSeries] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const [window, setWindow] = useState(20);

  useEffect(() => {
    if (!sel) return;
    let cancelled = false;
    setLoading(true); setErr(""); setSeries(null);
    fetch(`/api/flows?code=${sel.c}&pages=20`)
      .then(r => r.json())
      .then(d => {
        if (cancelled) return;
        if (d.error || !d.series || !d.series.length) { setErr(d.error || "수급 데이터 없음"); }
        else setSeries(d.series);
      })
      .catch(e => { if (!cancelled) setErr(String(e.message || e)); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [sel]);

  const traj = useMemo(() => (series ? computeFlowTrajectory(series, window, 6) : null), [series, window]);

  const lim = useMemo(() => {
    if (!traj) return 2;
    const mx = Math.max(...traj.pts.flatMap(p => [Math.abs(p.x), Math.abs(p.y)]), 1.2);
    return Math.ceil((mx + 0.3) * 2) / 2;
  }, [traj]);

  const qa = [
    { x1: 0, x2: lim, y1: 0, y2: lim, c: "#c0392b", t: "모멘텀", pos: "insideTopRight" },
    { x1: 0, x2: lim, y1: -lim, y2: 0, c: "#0a7c59", t: "매집", pos: "insideBottomRight" },
    { x1: -lim, x2: 0, y1: 0, y2: lim, c: "#b8860b", t: "분배", pos: "insideTopLeft" },
    { x1: -lim, x2: 0, y1: -lim, y2: 0, c: "#888", t: "침체", pos: "insideBottomLeft" },
  ];

  return (
    <div>
      <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap", marginBottom: 16 }}>
        <h2 style={{ margin: 0, fontSize: 18, fontWeight: 800 }}>📊 수급 궤적</h2>
        <StockSearch onPick={setSel} />
        {sel && (
          <div style={{ display: "flex", border: "1px solid #e2e2e2", borderRadius: 8, overflow: "hidden" }}>
            {WINDOWS.map(([v, lab]) => (
              <button key={v} onClick={() => setWindow(v)} style={{
                padding: "6px 11px", border: "none", cursor: "pointer", fontSize: 12, fontWeight: 700,
                background: window === v ? "#1a1a2e" : "#fff", color: window === v ? "#fff" : "#888",
              }}>{lab}</button>
            ))}
          </div>
        )}
      </div>

      {!sel ? (
        <div style={{ padding: 50, textAlign: "center", color: "#aaa", background: "#fafafa", borderRadius: 12, border: "1px dashed #ddd" }}>
          종목을 검색하면 <b>외국인+기관 순매수 vs 주가</b> 롤링Z 궤적을 그립니다.
        </div>
      ) : loading ? (
        <div style={{ padding: 50, textAlign: "center", color: "#aaa" }}>수급 데이터 불러오는 중…</div>
      ) : err ? (
        <div style={{ padding: 40, textAlign: "center", color: "#c0392b" }}>⚠️ {err}</div>
      ) : traj ? (
        <div style={{ display: "grid", gridTemplateColumns: "minmax(0, 1.4fr) minmax(0, 1fr)", gap: 20, alignItems: "start" }}>
          {/* 궤적 차트 */}
          <div style={{ background: "#fff", border: "1px solid #eee", borderRadius: 12, padding: 14 }}>
            <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 4 }}>{sel.n} <span style={{ color: "#aaa", fontWeight: 400 }}>{sel.c} · 롤링 {window}일 · 6구간</span></div>
            <ResponsiveContainer width="100%" height={380}>
              <ScatterChart margin={{ top: 10, right: 16, bottom: 20, left: 6 }}>
                <XAxis type="number" dataKey="x" domain={[-lim, lim]} tick={{ fontSize: 10, fill: "#999" }}
                  label={{ value: "외국인+기관 순매수 Z →", position: "bottom", fontSize: 11, fill: "#777" }} />
                <YAxis type="number" dataKey="y" domain={[-lim, lim]} tick={{ fontSize: 10, fill: "#999" }}
                  label={{ value: "주가 Z →", angle: -90, position: "left", fontSize: 11, fill: "#777" }} />
                <ZAxis range={[80, 80]} />
                {qa.map((q, i) => (
                  <ReferenceArea key={i} x1={q.x1} x2={q.x2} y1={q.y1} y2={q.y2} fill={q.c} fillOpacity={0.06}
                    label={{ value: q.t, position: q.pos, fill: q.c, fontSize: 11, fontWeight: 700 }} />
                ))}
                <ReferenceLine x={0} stroke="#ccc" /><ReferenceLine y={0} stroke="#ccc" />
                <Tooltip cursor={{ strokeDasharray: "3 3" }}
                  formatter={(v, n) => [v.toFixed(2), n === "x" ? "수급Z" : "주가Z"]}
                  labelFormatter={() => ""} />
                <Scatter data={traj.pts} line={{ stroke: "#1a5f7a", strokeWidth: 2 }} lineType="joint" fill="#1a5f7a">
                  {traj.pts.map((p, i) => <Cell key={i} fill={i === traj.pts.length - 1 ? "#e84040" : "#1a5f7a"} />)}
                  <LabelList dataKey="idx" position="top" style={{ fontSize: 10, fill: "#555", fontWeight: 700 }} />
                </Scatter>
              </ScatterChart>
            </ResponsiveContainer>
            <div style={{ fontSize: 10, color: "#bbb", textAlign: "right" }}>①={traj.pts[0].dateStart} → ⑥(빨강)={traj.pts[traj.pts.length - 1].dateEnd} 현재</div>
          </div>

          {/* 해석 카드 */}
          <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            <div style={{ background: traj.quadrant.color, color: "#fff", borderRadius: 12, padding: "16px 18px" }}>
              <div style={{ fontSize: 18, fontWeight: 800 }}>{traj.quadrant.label}</div>
              <div style={{ fontSize: 12, opacity: 0.9, marginTop: 4 }}>{traj.quadrant.desc}</div>
            </div>
            <div style={{ background: "#fff", border: "1px solid #eee", borderRadius: 12, padding: 16, fontSize: 13 }}>
              <div style={{ display: "flex", justifyContent: "space-between", padding: "5px 0" }}><span style={{ color: "#888" }}>현재 수급 Z</span><b style={{ color: traj.zsNow >= 0 ? "#c0392b" : "#1f5fd0" }}>{traj.zsNow >= 0 ? "+" : ""}{traj.zsNow.toFixed(2)}</b></div>
              <div style={{ display: "flex", justifyContent: "space-between", padding: "5px 0" }}><span style={{ color: "#888" }}>현재 주가 Z</span><b style={{ color: traj.zpNow >= 0 ? "#c0392b" : "#1f5fd0" }}>{traj.zpNow >= 0 ? "+" : ""}{traj.zpNow.toFixed(2)}</b></div>
              <hr style={{ border: "none", borderTop: "1px solid #f2f2f2", margin: "8px 0" }} />
              <div style={{ display: "flex", justifyContent: "space-between", padding: "5px 0" }}><span style={{ color: "#888" }}>🟢 외국인 순매수(최근일)</span><b style={{ color: traj.last.foreign >= 0 ? "#c0392b" : "#1f5fd0" }}>{traj.last.foreign.toLocaleString()}주</b></div>
              <div style={{ display: "flex", justifyContent: "space-between", padding: "5px 0" }}><span style={{ color: "#888" }}>🏦 기관 순매수(최근일)</span><b style={{ color: traj.last.inst >= 0 ? "#c0392b" : "#1f5fd0" }}>{traj.last.inst.toLocaleString()}주</b></div>
              <div style={{ fontSize: 10, color: "#bbb", marginTop: 8 }}>{traj.last.date} 기준 · {traj.n}거래일 (네이버 금융)</div>
            </div>
          </div>
        </div>
      ) : (
        <div style={{ padding: 40, textAlign: "center", color: "#aaa" }}>궤적 계산에 데이터가 부족합니다 (롤링 {window}일 필요).</div>
      )}

      <RelativeStrength />
    </div>
  );
}
